import ExpandMore from "@mui/icons-material/ExpandMore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import { indigo } from "../../colors";
import { Feature } from "../../featureFlags/featureFlagConfig";
import useFeatureFlag from "../../featureFlags/useFeatureFlag";
import { SWITCHABLE_DOMAINS, getDomainLabel } from "./domainMeta";
import DomainSwitchModal from "./DomainSwitchModal";
import Roles from "./Roles";
import useAuthorizationManager from "./useAuthorizationManager";
import useUserContext from "./useUserContext";

/**
 * Top-right domain indicator / switcher.
 *
 * Users who belong to more than one domain (Commercial / Medical / PMR / Retros)
 * get a dropdown listing the domains they can move to. Users with a single
 * domain see a static label of the domain they are currently in.
 */
const DomainDropdown = (): JSX.Element | null => {
  const AuthorizationManager = useAuthorizationManager();
  const user = useUserContext();
  const theme = useTheme();
  const pmr_feature = useFeatureFlag(Feature.PrimaryMarketResearch);
  const retros_feature = useFeatureFlag(Feature.Retros);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pendingDomain, setPendingDomain] = useState<string | null>(null);
  const open = Boolean(anchorEl);

  const { assignedRoles, selectedRoles } = user;

  const availableDomains = SWITCHABLE_DOMAINS.filter((domain) => {
    if (!assignedRoles.includes(domain)) return false;
    if (domain === Roles.PRIMARY_MARKET_RESEARCH && !pmr_feature.enabled) return false;
    if (domain === Roles.USLT_RETRO && !retros_feature.enabled) return false;
    return true;
  });

  // Nothing to render if the user has no recognised domain (defensive).
  if (availableDomains.length === 0) return null;

  const activeDomain = availableDomains.find((domain) => selectedRoles.includes(domain)) ?? availableDomains[0];

  // Single domain: static label, nothing to switch to.
  if (availableDomains.length < 2) {
    return (
      <Typography sx={{ color: indigo[900], fontWeight: 600, fontSize: "14px", px: 1.5 }}>
        {getDomainLabel(activeDomain)}
      </Typography>
    );
  }

  const handleSelect = (domain: string) => {
    setAnchorEl(null);
    if (domain !== activeDomain) {
      setPendingDomain(domain);
    }
  };

  const handleLogout = () => {
    setPendingDomain(null);
    AuthorizationManager.logout();
  };

  return (
    <>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<ExpandMore />}
        sx={{
          textTransform: "none",
          color: indigo[900],
          fontWeight: 600,
          fontSize: "14px",
          borderRadius: "8px",
          px: 1.5,
          height: "36px",
          border: `1px solid ${theme.palette.grey[200]}`,
          "&:hover": { backgroundColor: indigo[50] },
        }}
      >
        {getDomainLabel(activeDomain)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ "& .MuiPaper-root": { borderRadius: 2, minWidth: 200, paddingTop: "5px" } }}
      >
        {availableDomains.map((domain) => (
          <MenuItem key={domain} selected={domain === activeDomain} onClick={() => handleSelect(domain)} disableRipple>
            <ListItemText
              primary={getDomainLabel(domain)}
              primaryTypographyProps={{ fontSize: "14px", fontWeight: domain === activeDomain ? 700 : 400 }}
            />
          </MenuItem>
        ))}
      </Menu>

      <DomainSwitchModal
        open={Boolean(pendingDomain)}
        fromRole={activeDomain}
        toRole={pendingDomain ?? activeDomain}
        onLogout={handleLogout}
        onCancel={() => setPendingDomain(null)}
      />
    </>
  );
};

export default DomainDropdown;
